/**
 * The Channels tab's search rules, shared by the desktop (`ChannelsBody`) and mobile (`ChannelsMobile`)
 * surfaces. Pure — no React, no i18n instance — so it is unit-testable without the app's dependency
 * graph, and so both surfaces narrow identically.
 *
 * Shared 1:1 with iOS `ChannelsTabView.filterSections` / `AiCodersConnect.itemTitleKeys` and Android
 * `filterChannelSections`. The channels search matches on SECTION text, which is why it is separate
 * from the Tools tab's `matchesConnectorQuery` (name + toolkit slug).
 */

/**
 * The AI-coder card titles, in render order: Claude Code, Claude web & desktop, ChatGPT. The Channels
 * search matches that section on THESE, not on its "AI coders" section title — a query for a coder name
 * is what surfaces the section, and the same keys then narrow to the matching card.
 */
export const AI_CODERS_ITEM_TITLE_KEYS = [
  'connections.claude.title',
  'connections.connect.claudeWeb.title',
  'connections.connect.chatgpt.title',
] as const;

export type AiCoderTitleKey = (typeof AI_CODERS_ITEM_TITLE_KEYS)[number];

/** Trim + lowercase `contains`; an empty query matches everything. Mirrors native `AiCodersConnect.matches`. */
export function matchesChannelTitle(resolvedTitle: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || resolvedTitle.toLowerCase().includes(q);
}

/**
 * Keep the sections whose resolved `title` contains the query; an empty/blank query keeps all, in the
 * order given. `title` resolves a section's haystack — its own display title for most sections, its
 * ITEM titles where the section renders named cards (see `AI_CODERS_ITEM_TITLE_KEYS`).
 */
export function filterChannelSections<T>(order: readonly T[], query: string, title: (slug: T) => string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...order];
  return order.filter((slug) => title(slug).toLowerCase().includes(q));
}
